// skills/woodcutting/woodcutting.js
// Woodcutting skill logic

import { TREES, AXES } from './woodcuttingData.js';
import { gameState } from '../../game/gameState.js';
import { addXP } from '../../systems/experience.js';
import { SKILLS } from '../../game/constants.js';
import logger from '../../utils/logger.js';
import { awardDrops, canFitItem } from '../../systems/drops.js';
import { rollForCorruption } from '../../systems/corruption.js';
import { pilferingSkill } from '../pilfering/pilfering.js';
import { bountySkill } from '../bounty/bounty.js';
import { toastWarning, toastSuccess } from '../../ui/toast.js';
import { getTalentLootBonus, getTalentFlatBonus, getTalentProcChance, rollTalentProc, getStackingSpeedBonus, incrementStackCount, resetStackCount, rollGlobalRareLoot } from '../../systems/talentTree/talentTree.js';
import { isBloomholdEligible } from '../../systems/bloomhold.js';
import { recordTalentProc } from '../../ui/talentActivity.js';
import { BaseSkill } from '../../core/BaseSkill.js';

class WoodcuttingSkill extends BaseSkill {
    constructor() {
        super(SKILLS.WOODCUTTING, AXES, 'COMMON');
        // Kindling Master ash counter (tracks chops since last ash award)
        this.kindlingMasterCounter = 0;
    }

    /**
     * Get the currently equipped axe object (alias for BaseSkill.getCurrentTool)
     */
    get currentAxe() {
        return this.getCurrentTool();
    }

    /**
     * Roll for talent proc effects and apply them
     * Returns object with proc results
     */
    rollWoodcuttingProcs(tree) {
        const procs = {
            doubleLogs: false,
            seedDrop: null,
            ancientSeedDrop: false,
            timberProc: false
        };

        // Check if at max stacking bonus for Rhythmic Precision
        const rhythmicPrecision = getTalentFlatBonus(SKILLS.WOODCUTTING, 'rhythmicPrecision');
        const stackingCapBonus = getTalentFlatBonus(SKILLS.WOODCUTTING, 'stackingCapBonus');
        const maxStack = 0.10 + stackingCapBonus; // Base 10% + Extended Rhythm bonus
        const currentStack = getStackingSpeedBonus(SKILLS.WOODCUTTING);
        const atMaxStack = currentStack >= maxStack - 0.001; // Small tolerance for float comparison
        // Rhythmic Precision adds flat +8% to all proc chances when at max stack
        const flatProcBonus = (rhythmicPrecision > 0 && atMaxStack) ? rhythmicPrecision : 0;

        // Double logs - 5% per rank (+ Rhythmic Precision at max stack)
        if (this.rollProcWithBonus('doubleLogs', flatProcBonus)) {
            procs.doubleLogs = true;
        }

        // Seed drop - 4% per rank (+ Rhythmic Precision at max stack)
        if (this.rollProcWithBonus('seedDrop', flatProcBonus)) {
            // Get appropriate seed for the tree type
            procs.seedDrop = this.getSeedForTree(tree);
        }

        // Ancient Seed drop - 0.5% per rank (+ Rhythmic Precision at max stack)
        if (this.rollProcWithBonus('ancientSeedDrop', flatProcBonus)) {
            procs.ancientSeedDrop = true;
        }

        // Timber! - 3% chance for 5x logs (+ Rhythmic Precision at max stack)
        if (this.rollProcWithBonus('timberProc', flatProcBonus)) {
            procs.timberProc = true;
        }

        return procs;
    }

    /**
     * Roll a proc with additive flat bonus (for Rhythmic Precision)
     */
    rollProcWithBonus(procType, flatBonus) {
        let chance = getTalentProcChance(SKILLS.WOODCUTTING, procType);
        if (chance <= 0 && flatBonus <= 0) return false;
        // Add flat bonus (e.g., Rhythmic Precision's +8%)
        chance += flatBonus;
        return Math.random() < chance;
    }

    /**
     * Get the appropriate seed for a tree type
     */
    getSeedForTree(tree) {
        // Map tree IDs to seed IDs (based on actual woodcuttingData.js trees)
        const seedMap = {
            'common_timber_tree': 'ironwood_seed',  // Common timber has no seed, fallback to ironwood
            'ironwood_tree': 'ironwood_seed',
            'softbark_tree': 'softbark_seed',
            'goldleaf_tree': 'goldleaf_seed',
            'redgrain_tree': 'redgrain_seed',
            'gravewood_tree': 'gravewood_seed',
            'arcanewood_tree': 'arcanewood_seed'
        };
        return seedMap[tree.id] || 'ironwood_seed';
    }

    /**
     * Check if player can chop a tree
     */
    canChop(treeId) {
        // Find tree by id property, not by object key
        const tree = Object.values(TREES).find(t => t.id === treeId);
        if (!tree) return { success: false, reason: 'Tree not found' };

        const skill = gameState.getSkill(SKILLS.WOODCUTTING);
        if (skill.level < tree.level) {
            return { success: false, reason: `Requires level ${tree.level} Woodcutting` };
        }

        // Check if primary drop can fit in inventory
        const primaryDrop = tree.drops[0];
        if (primaryDrop && !canFitItem(primaryDrop.itemId)) {
            return { success: false, reason: 'Inventory full' };
        }

        return { success: true };
    }

    /**
     * Start chopping a tree
     */
    startChopping(treeId) {
        // Find tree by id property, not by object key
        const tree = Object.values(TREES).find(t => t.id === treeId);
        const canChop = this.canChop(treeId);

        if (!canChop.success) {
            logger.debug('Cannot chop:', canChop.reason);
            return false;
        }

        // Calculate chopping time including talent bonuses
        const baseTime = tree.baseTime;
        const speedMultiplier = this.getEffectiveSpeedMultiplier();
        const choppingTime = baseTime / speedMultiplier;

        // Create chopping action
        const action = {
            type: 'woodcutting',
            name: `Chopping ${tree.name}`,
            tree: tree,
            treeId: treeId.toUpperCase(),
            progress: 0,
            duration: choppingTime,
            startTime: Date.now(),
            forceComplete: false,

            // Tick function called every game tick
            // speedMultiplier param includes admin speed + corruption (applied in tick.js)
            tick: (speedMultiplier = 1) => {
                // Check for force complete (used by admin panel)
                if (action.forceComplete) {
                    action.forceComplete = false;
                    const result = this.completeChopping(tree);
                    if (result.inventoryFull) {
                        toastWarning('Inventory full - woodcutting stopped');
                        gameState.clearAction();
                        return;
                    }
                    const canContinue = this.canChop(treeId);
                    if (canContinue.success) {
                        this.startChopping(treeId);
                    } else {
                        gameState.clearAction();
                    }
                    return;
                }

                // Apply speed multiplier to elapsed time
                const elapsed = (Date.now() - action.startTime) * speedMultiplier;
                action.progress = Math.min(elapsed / action.duration, 1);

                // Complete when progress reaches 1
                if (action.progress >= 1) {
                    const result = this.completeChopping(tree);
                    if (result.inventoryFull) {
                        toastWarning('Inventory full - woodcutting stopped');
                        gameState.clearAction();
                        return;
                    }
                    const canContinue = this.canChop(treeId);
                    if (canContinue.success) {
                        this.startChopping(treeId);
                    } else {
                        gameState.clearAction();
                    }
                }
            }
        };

        gameState.setAction(action);
        return true;
    }

    /**
     * Complete a chopping action
     * @returns {{ inventoryFull: boolean }} Whether inventory became full
     */
    completeChopping(tree) {
        // Roll for talent procs first
        const procs = this.rollWoodcuttingProcs(tree);

        // Award XP (pass tree.id for admin node XP override)
        const xpResult = addXP(SKILLS.WOODCUTTING, tree.xp, tree.id);

        // Award drops (handles corruption loot multiplier centrally)
        const dropResult = awardDrops(SKILLS.WOODCUTTING, tree.drops);

        // Apply talent proc bonuses
        const primaryLogId = tree.drops[0]?.itemId;

        if (procs.doubleLogs) {
            // Award drops again for double logs
            awardDrops(SKILLS.WOODCUTTING, tree.drops);
            logger.game('Sharp Edge proc! Double logs!');
            recordTalentProc(SKILLS.WOODCUTTING, 'Sharp Edge', null, null, { itemId: primaryLogId, quantity: 2 });
        }

        if (procs.timberProc) {
            // Award 4 more drops (5x total, already got 1 above)
            for (let i = 0; i < 4; i++) {
                awardDrops(SKILLS.WOODCUTTING, tree.drops);
            }
            toastSuccess('TIMBER! Massive haul of logs!');
            recordTalentProc(SKILLS.WOODCUTTING, 'Timber!', null, null, { itemId: primaryLogId, quantity: 5 });

            // Nature's Blessing - Timber! also drops 1-3 random seeds
            const naturesBlessing = getTalentFlatBonus(SKILLS.WOODCUTTING, 'naturesBlessing');
            if (naturesBlessing > 0) {
                const seedCount = Math.floor(Math.random() * 3) + 1; // 1-3 seeds
                const randomSeeds = this.getRandomSeeds(seedCount);
                for (const seed of randomSeeds) {
                    this.awardSeed(seed);
                }
                recordTalentProc(SKILLS.WOODCUTTING, "Nature's Blessing", null, null, { itemId: randomSeeds[0], quantity: seedCount });
            }
        }

        // Seed drop handling with Fertile Grove (auto-deposit to BloomHold)
        if (procs.seedDrop) {
            this.awardSeed(procs.seedDrop);
            logger.game(`Deep Roots proc! Found a ${procs.seedDrop.replace(/_/g, ' ')}!`);
            recordTalentProc(SKILLS.WOODCUTTING, 'Deep Roots', null, null, { itemId: procs.seedDrop, quantity: 1 });
        }

        // Ancient Seed drop
        if (procs.ancientSeedDrop) {
            this.awardSeed('ancient_seed');
            toastSuccess('Ancient Seed discovered! A mysterious amalgamation...');
            recordTalentProc(SKILLS.WOODCUTTING, 'Ancient Discovery', null, null, { itemId: 'ancient_seed', quantity: 1 });
        }

        // Kindling Master - guaranteed ash based on tree tier
        const kindlingMaster = getTalentFlatBonus(SKILLS.WOODCUTTING, 'kindlingMaster');
        if (kindlingMaster > 0) {
            this.kindlingMasterCounter++;
            // High tier trees (level >= 50): 1 ash per 5 chops
            // Low tier trees (level < 50): 1 ash per 8 chops
            const threshold = tree.level >= 50 ? 5 : 8;
            if (this.kindlingMasterCounter >= threshold) {
                gameState.addItem('ashes', 1);
                bountySkill.trackItemProgress('ashes', 1);
                this.kindlingMasterCounter = 0;
                logger.game('Kindling Master! Collected ashes from the forest floor.');
                recordTalentProc(SKILLS.WOODCUTTING, 'Kindling Master', null, null, { itemId: 'ashes', quantity: 1 });
            }
        }

        // Treasure Nose (Husbandry) - global rare loot bonus (seeds)
        if (rollGlobalRareLoot()) {
            const seed = this.getSeedForTree(tree);
            if (seed) {
                this.awardSeed(seed);
                recordTalentProc(SKILLS.WOODCUTTING, 'Treasure Nose', null, null, { itemId: seed, quantity: 1 });
            }
        }

        // Roll for corruption proc
        rollForCorruption(SKILLS.WOODCUTTING);

        // Check for pilferer contract assistance (rolls once per action completion)
        pilferingSkill.tryContractAssist('woodcutting');

        // Increment stacking speed count (Sustained Effort talent)
        // Extended Rhythm cap bonus is handled internally by incrementStackCount
        incrementStackCount(SKILLS.WOODCUTTING, tree.id);

        // Notify UI of completion
        this.onChoppingComplete(tree, xpResult);

        return { inventoryFull: dropResult.inventoryFull };
    }

    /**
     * Award a seed, respecting Fertile Grove (auto-deposit to BloomHold)
     */
    awardSeed(seedId) {
        const fertileGrove = getTalentFlatBonus(SKILLS.WOODCUTTING, 'seedsToBloomHold');

        // Check if Fertile Grove is active and BloomHold is unlocked
        if (fertileGrove > 0 && gameState.isBloomholdUnlocked() && isBloomholdEligible(seedId)) {
            // Try to add directly to BloomHold
            const result = gameState.addBloomholdItem(seedId, 1);
            if (result.success) {
                logger.game(`Fertile Grove: ${seedId.replace(/_/g, ' ')} sent to BloomHold!`);
                return;
            }
            // BloomHold full, fall through to inventory
        }

        // Add to inventory
        gameState.addItem(seedId, 1);
        bountySkill.trackItemProgress(seedId, 1);
    }

    /**
     * Get random seeds for Nature's Blessing
     */
    getRandomSeeds(count) {
        // Use actual tree seeds from seeds.js
        const allSeeds = [
            'ironwood_seed', 'softbark_seed', 'goldleaf_seed',
            'redgrain_seed', 'gravewood_seed', 'arcanewood_seed'
        ];
        const seeds = [];
        for (let i = 0; i < count; i++) {
            const randomIndex = Math.floor(Math.random() * allSeeds.length);
            seeds.push(allSeeds[randomIndex]);
        }
        return seeds;
    }

    /**
     * Stop current chopping action
     */
    stopChopping() {
        resetStackCount(SKILLS.WOODCUTTING);
        gameState.clearAction();
    }

    /**
     * Purchase or equip an axe
     */
    upgradeAxe(axeId) {
        const axe = Object.values(AXES).find(a => a.id === axeId);
        if (!axe) return { success: false, reason: 'Invalid axe' };

        const skill = gameState.getSkill(SKILLS.WOODCUTTING);
        if (skill.level < axe.level) {
            return { success: false, reason: `Requires level ${axe.level} Woodcutting` };
        }

        // Check if already owned
        if (gameState.hasPurchasedTool(SKILLS.WOODCUTTING, axeId)) {
            // Just equip it
            gameState.equipTool(SKILLS.WOODCUTTING, axeId);
            return { success: true, action: 'equipped' };
        }

        // Try to purchase
        const purchaseResult = gameState.purchaseTool(SKILLS.WOODCUTTING, axeId, axe.price);
        if (!purchaseResult.success) {
            return purchaseResult;
        }

        // Auto-equip after purchase
        gameState.equipTool(SKILLS.WOODCUTTING, axeId);
        return { success: true, action: 'purchased' };
    }

    /**
     * Get available trees for player's level
     */
    getAvailableTrees() {
        const skill = gameState.getSkill(SKILLS.WOODCUTTING);
        return Object.values(TREES).filter(tree => tree.level <= skill.level);
    }

    /**
     * Callback for when chopping completes (override in UI)
     */
    onChoppingComplete(tree, xpResult) {
        logger.game(`Chopped ${tree.name}! +${tree.xp} XP`);
        if (xpResult.leveledUp) {
            logger.game(`Woodcutting level up! Now level ${xpResult.newLevel}`);
        }
    }
}

// Singleton instance
export const woodcuttingSkill = new WoodcuttingSkill();
export default woodcuttingSkill;